import { useEffect, useState } from 'react';
import { api, ApiError, type CreatedToken, type Scope, type TokenSummary } from '../api';
import { Alert, Badge, Button, Card, Input, PageHeader, Select, Spinner } from '../components/ui';

const SCOPE_LABELS: Record<Scope, string> = {
  read: 'read — browse including your private packages',
  publish: 'publish — publish & update your packages',
  'manage:packages': 'manage:packages — yank & change visibility',
};

export default function Tokens() {
  const [tokens, setTokens] = useState<TokenSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<CreatedToken | null>(null);

  // create-form state
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<Scope[]>(['publish']);
  const [expiry, setExpiry] = useState('never');

  async function load() {
    try {
      setTokens(await api.tokens());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load tokens');
    }
  }
  useEffect(() => {
    void load();
  }, []);

  function toggleScope(s: Scope) {
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }

  async function create() {
    setError(null);
    if (!name.trim()) return setError('Give the token a name.');
    if (scopes.length === 0) return setError('Pick at least one scope.');
    setCreating(true);
    try {
      const created = await api.createToken(
        name.trim(),
        scopes,
        expiry === 'never' ? undefined : Number(expiry),
      );
      setJustCreated(created);
      setName('');
      setScopes(['publish']);
      setExpiry('never');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create token');
    } finally {
      setCreating(false);
    }
  }

  async function revoke(id: string) {
    if (!confirm('Revoke this token? Anything using it will stop working immediately.')) return;
    await api.revokeToken(id).catch(() => {});
    await load();
  }

  return (
    <div>
      <PageHeader title="Access Tokens" subtitle="Personal Access Tokens authenticate the API, CLI, CI, and agents." />

      {justCreated && (
        <div className="mb-6">
          <Alert kind="success">
            <p className="font-semibold">Token created — copy it now. You won't see it again.</p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 overflow-x-auto rounded bg-white px-3 py-2 font-mono text-xs ring-1 ring-green-200">
                {justCreated.token}
              </code>
              <Button variant="secondary" onClick={() => navigator.clipboard?.writeText(justCreated.token)}>
                Copy
              </Button>
              <Button variant="ghost" onClick={() => setJustCreated(null)}>
                Done
              </Button>
            </div>
          </Alert>
        </div>
      )}

      {error && (
        <div className="mb-4">
          <Alert kind="error">{error}</Alert>
        </div>
      )}

      <Card className="mb-8">
        <h3 className="mb-4 font-semibold text-slate-900">Create a token</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Name</label>
            <Input placeholder="e.g. CI publish" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Expires</label>
            <Select value={expiry} onChange={(e) => setExpiry(e.target.value)}>
              <option value="never">Never</option>
              <option value="30">In 30 days</option>
              <option value="90">In 90 days</option>
              <option value="365">In 1 year</option>
            </Select>
          </div>
        </div>
        <fieldset className="mt-4">
          <legend className="mb-2 text-sm font-medium text-slate-700">Scopes</legend>
          <div className="space-y-2">
            {(Object.keys(SCOPE_LABELS) as Scope[]).map((s) => (
              <label key={s} className="flex cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-slate-50">
                <input type="checkbox" checked={scopes.includes(s)} onChange={() => toggleScope(s)} className="h-4 w-4 rounded text-brand-600" />
                <span className="text-sm text-slate-600">
                  <code className="font-semibold text-slate-800">{s}</code>
                  <span className="ml-1 text-slate-400">— {SCOPE_LABELS[s].split('—')[1]}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="mt-4">
          <Button onClick={create} disabled={creating}>
            {creating ? 'Creating…' : 'Create token'}
          </Button>
        </div>
      </Card>

      <h3 className="mb-3 font-semibold text-slate-900">Your tokens</h3>
      {!tokens ? (
        <Spinner />
      ) : tokens.length === 0 ? (
        <p className="text-sm text-slate-500">No tokens yet.</p>
      ) : (
        <div className="space-y-3">
          {tokens.map((t) => {
            const dead = !!t.revokedAt || (t.expiresAt !== null && t.expiresAt < new Date().toISOString());
            return (
              <Card key={t.id} className="flex items-center justify-between py-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-900">{t.name}</span>
                    <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-500">
                      {t.prefix}…
                    </code>
                    {dead ? <Badge color="red">{t.revokedAt ? 'revoked' : 'expired'}</Badge> : <Badge color="green">active</Badge>}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {t.scopes.map((s) => (
                      <Badge key={s} color="blue">
                        {s}
                      </Badge>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-slate-400">
                    Created {fmt(t.createdAt)} · Last used {t.lastUsedAt ? fmt(t.lastUsedAt) : 'never'}
                    {t.expiresAt && ` · Expires ${fmt(t.expiresAt)}`}
                  </p>
                </div>
                {!dead && (
                  <Button variant="danger" onClick={() => revoke(t.id)}>
                    Revoke
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
